import React, {ReactElement, useEffect, useMemo} from 'react';
import {MiddlewareFn} from 'src/hooks/useFunctionChain';
import GlobalNotification, {NotificationFnResult} from 'src/components/GlobalNotification';
import {Banners} from 'src/types/store';
import {isNull} from 'lodash';
import {Link, RouteComponentProps} from 'react-router-dom';
import {formatMobile} from 'src/utils/formatters';

type StudentNotificationBannerProps = RouteComponentProps & {
  allowedNotificationIds?: string[];
  banners: Banners;
  getUserInfo: () => void;
  sendVerificationSMS: (mobile: string) => void;
  transfer: boolean;
};

type MiddlewareFnContext = StudentNotificationBannerProps & {
  hideVerifiedConfirmation?: () => void;
  portfolioItems?: number;
  sendVerificationEmail?: () => void;
  updateContactInfoUpdatedAt?: () => void;
  contactUpdateDate: Date;
  emailVerified: boolean;
  emptyIncomeStatus: boolean;
  link: (type: 'contact' | 'profile') => string;
  pathname: string;
  sendVerificationSMSOnClick: () => void;
  yearAgo: Date;
};

const CONTACT_URL = '/account-settings/contact';
const PROFILE_URL = '/account-settings/profile';

const notificationChain: {
  id: string;
  fn: MiddlewareFn<MiddlewareFnContext, NotificationFnResult>;
}[] = [
  {
    id: 'email',
    fn: (ctx): NotificationFnResult => {
      if (!ctx.banners.email) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              Add your email address.
              <a
                href={ctx.link('contact')}
                className="portfolio-banner-notification-link"
                data-testid="portfolio-banner-notification-link"
              >
                ADD
              </a>
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'verified',
    fn: (ctx): NotificationFnResult => {
      if (ctx.emailVerified && ctx.banners.showVerifiedConfirmation !== false) {
        return {
          timeout: 3000,
          onDismiss: ctx.hideVerifiedConfirmation,
          notification: (
            <span className="portfolio-banner-notification-text">
              Thank you for confirming your email address!
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'contact-reminder',
    fn: (ctx): NotificationFnResult => {
      if ((ctx.portfolioItems || 0) >= 2 && ctx.contactUpdateDate < ctx.yearAgo) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              Update your contact information.
              <a
                href={ctx.link('contact')}
                className="portfolio-banner-notification-link"
                onClick={ctx.updateContactInfoUpdatedAt}
                data-testid="portfolio-banner-notification-link"
              >
                UPDATE
              </a>
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'profile',
    fn: (ctx): NotificationFnResult => {
      if (
        isNull(ctx.banners.ethnicity) ||
        isNull(ctx.banners.isFirstGen) ||
        ctx.emptyIncomeStatus
      ) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              Complete your user profile.
              <a href={ctx.link('profile')} className="portfolio-banner-notification-link">
                UPDATE
              </a>
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'mobile',
    fn: (ctx): NotificationFnResult => {
      if (!ctx.banners.mobile) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              Add your mobile number.
              <Link to={ctx.link('contact')} className="portfolio-banner-notification-link">
                ADD
              </Link>
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'verify',
    fn: (ctx): NotificationFnResult => {
      if (!ctx.banners.emailVerifiedAt && ctx.banners.sendingVerifyEmail !== false) {
        if (ctx.banners.sendingVerifyEmail) {
          return {
            notification: (
              <span className="portfolio-banner-notification-text">
                Thanks! We have sent you a verification email.
              </span>
            ),
          };
        }
      }
    },
  },
  {
    id: 'verify',
    fn: (ctx): NotificationFnResult => {
      if (!ctx.banners.emailVerifiedAt && ctx.banners.sendingVerifyEmail !== false) {
        if (!ctx.banners.sendingVerifyEmail) {
          return {
            notification: (
              <span className="portfolio-banner-notification-body">
                Verify your email address.
                <button
                  className="portfolio-banner-notification-button"
                  onClick={ctx.sendVerificationEmail}
                >
                  RESEND EMAIL
                </button>
              </span>
            ),
          };
        }
      }
    },
  },
  {
    id: 'parent-email',
    fn: (ctx): NotificationFnResult => {
      if (!ctx.transfer && !ctx.banners.parentEmail) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              Add your parent’s email address.
              <a
                href={CONTACT_URL}
                className="portfolio-banner-notification-link"
                data-testid="portfolio-banner-notification-link"
              >
                ADD
              </a>
            </span>
          ),
        };
      }
    },
  },
  {
    id: 'mobile-verify',
    fn: (ctx): NotificationFnResult => {
      if (ctx.banners.country === 'US' && ctx.banners.mobile && !ctx.banners.mobileVerified) {
        return {
          notification: (
            <span className="portfolio-banner-notification-body">
              {`Is ${formatMobile(ctx.banners.mobile)} your number?`}
              <span>
                <Link to={ctx.link('contact')} className="portfolio-banner-notification-link">
                  UPDATE
                </Link>
                {' | '}
                <Link
                  to={ctx.pathname.concat('/verify-phone')}
                  className="portfolio-banner-notification-link"
                  onClick={ctx.sendVerificationSMSOnClick}
                >
                  VERIFY
                </Link>
              </span>
            </span>
          ),
        };
      }
    },
  },
];

export default function StudentNotificationBanner(
  props: StudentNotificationBannerProps
): ReactElement | null {
  useEffect(props.getUserInfo, []);
  const emailVerified = useMemo(() => {
    const location = props.location;
    const path = location.pathname + location.search;
    return path.match(/email_verified=true/gm);
  }, []);

  const notificationFns = useMemo<MiddlewareFn<MiddlewareFnContext, NotificationFnResult>[]>(() => {
    if (props.allowedNotificationIds) {
      const set = new Set(props.allowedNotificationIds);
      return notificationChain.filter((fn) => set.has(fn.id)).map((fn) => fn.fn);
    }

    return notificationChain.map((fn) => fn.fn);
  }, [props.allowedNotificationIds]);

  const banners = props.banners;
  if (
    banners.loading ||
    !banners.highSchoolInfo ||
    !banners.communityCollegeInfo ||
    !banners.undergraduateInfo
  ) {
    return null;
  }

  const sendVerificationSMSOnClick = (): void => props.sendVerificationSMS(props.banners.mobile);
  const link = (type: 'contact' | 'profile'): string => {
    switch (type) {
      case 'contact':
        return CONTACT_URL;
      case 'profile':
        return PROFILE_URL;
    }
  };

  const emptyIncomeStatus = props.transfer
    ? isNull(banners.communityCollegeInfo['lowIncome'])
    : isNull(banners.highSchoolInfo['lunchPlan']);

  const contactUpdateDate = banners.contactInfoUpdatedAt
    ? new Date(banners.contactInfoUpdatedAt * 1000)
    : new Date(banners.createdAt * 1000);

  const today = new Date();
  const yearAgo = new Date(today.setFullYear(today.getFullYear() - 1));

  const notificationContext: MiddlewareFnContext = {
    ...props,
    contactUpdateDate,
    emailVerified: Boolean(emailVerified),
    emptyIncomeStatus,
    link,
    pathname: props.location.pathname,
    sendVerificationSMSOnClick,
    yearAgo,
  };

  return (
    <div className="portfolio-banner-notification" data-testid="student-notification-banner">
      <GlobalNotification<MiddlewareFnContext>
        context={notificationContext}
        notificationChain={notificationFns}
      />
    </div>
  );
}

StudentNotificationBanner.defaultProps = {
  transfer: false,
};
